// Step 2 — build the load list. Two tabs: "From Storage" and "Manual".
// Manual tab uses cascading selects (same pattern as the product-launch
// Setup form): cable name → #conductors → section → color → GY section,
// and drum type → drum diameter → flange width — each dropdown only shows
// values that still have a matching record given the prior selections.

import React, { useMemo, useState } from 'react';
import {
  Row, Col, Form, Button, Table, Badge, Alert, Spinner, Card,
  ToggleButtonGroup, ToggleButton,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWarehouse, faIndustry, faRotate } from '@fortawesome/free-solid-svg-icons';
import { useQuery } from '@tanstack/react-query';

import WarehouseStockTable from '../WarehouseStockTable.js';
import { useDrums } from '../../../features/drums/hooks/useDrums.js';
import {
  fetchAllStandards,
  fetchCableWeight,
} from '../../../features/warehouse/truckSimApi.js';
import { typeBadgeColor, nextOrientation, drumSpecLabel } from '../../../features/warehouse/warehouseUtils.js';
import { formatProductLabel } from '../../../helpers/productLabel';

// Short user-facing label for each orientation mode.
const ORIENT_LABEL = {
  upright: 'upright',
  'side-x': 'side ▬',
  'side-z': 'side ▮',
  'long-x': 'long ▬',
  'long-z': 'long ▮',
};
const orientLabel = (item) =>
  ORIENT_LABEL[item.orientation] ||
  (item.type === 'drum' ? ORIENT_LABEL.upright : ORIENT_LABEL['long-x']);

const EUROPALLET_PHYSICAL = { widthM: 1.2, depthM: 0.8, heightM: 1.5 };

const LOADABLE_STATUSES = new Set(['available', 'reserved', 'placed', 'waiting']);

const sortNum = (a, b) => Number(a) - Number(b);
const uniqSorted = (arr, cmp = sortNum) => [...new Set(arr.filter(v => v != null && v !== ''))].sort(cmp);

export default function StepItems({ items, onChange }) {
  const [tab, setTab] = useState('storage');

  const removeLine = idx => onChange(items.filter((_, i) => i !== idx));
  const addLines = newItems => onChange([...items, ...newItems]);
  const rotateLine = idx => onChange(items.map((it, i) =>
    i === idx ? { ...it, orientation: nextOrientation(it, it.orientation) } : it
  ));

  return (
    <>
      <div className="mb-3">
        <ToggleButtonGroup
          type="radio"
          name="items-source"
          value={tab}
          onChange={setTab}
        >
          <ToggleButton
            id="items-source-storage"
            value="storage"
            variant={tab === 'storage' ? 'primary' : 'outline-primary'}
            className="px-4"
          >
            <FontAwesomeIcon icon={faWarehouse} className="me-2" />
            From Storage
          </ToggleButton>
          <ToggleButton
            id="items-source-manual"
            value="manual"
            variant={tab === 'manual' ? 'primary' : 'outline-primary'}
            className="px-4"
          >
            <FontAwesomeIcon icon={faIndustry} className="me-2" />
            Manual / From Production
          </ToggleButton>
        </ToggleButtonGroup>
      </div>

      {tab === 'storage' && (
        <StorageTab
          alreadyAdded={items.filter(i => i.source === 'storage').map(i => i._id)}
          onAdd={addLines}
        />
      )}
      {tab === 'manual' && <ManualTab onAdd={addLines} />}

      <hr />
      <h6 className="mb-2">Load list ({items.length})</h6>
      {items.length === 0 && (
        <Alert variant="light" className="border mb-0">
          No items yet. Add some from the tabs above.
        </Alert>
      )}
      {items.length > 0 && (
        <Table size="sm" hover responsive className="mb-0">
          <thead>
            <tr>
              <th>#</th>
              <th>Source</th>
              <th>Serial</th>
              <th>Type</th>
              <th>Cable</th>
              <th className="text-end">Length (m)</th>
              <th className="text-end">Weight (kg)</th>
              <th>Orientation</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={it._id || i}>
                <td>{i + 1}</td>
                <td>
                  <Badge bg={it.source === 'manual' ? 'warning' : 'info'} text="dark">
                    {it.source}
                  </Badge>
                </td>
                <td><code>{it.serial}</code></td>
                <td>
                  <Badge bg={typeBadgeColor(it.type)}>{it.type}</Badge>
                  {drumSpecLabel(it) && (
                    <div className="text-body-tertiary text-capitalize" style={{ fontSize: 11 }}>
                      {drumSpecLabel(it)}
                    </div>
                  )}
                </td>
                <td className="small">{it.cableName}</td>
                <td className="text-end">{Number(it.lengthMeters || 0).toFixed(0)}</td>
                <td className="text-end">{Number(it.weightKg || 0).toFixed(1)}</td>
                <td>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => rotateLine(i)}
                    title="Cycle orientation"
                  >
                    <FontAwesomeIcon icon={faRotate} className="me-1" />
                    {orientLabel(it)}
                  </Button>
                </td>
                <td>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline-danger"
                    onClick={() => removeLine(i)}
                  >
                    Remove
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Storage tab — select from real StorageItem documents.
// ---------------------------------------------------------------------------

function StorageTab({ alreadyAdded, onAdd }) {
  // Stock-table picker (Serial/Token · Product · Cond · Sect · Type · Length ·
  // Weight · For · Location) with a per-row "Add to load" button — same table
  // used across the warehouse pages.
  return (
    <WarehouseStockTable
      title="Warehouse stock — pick items to load"
      addedIds={alreadyAdded}
      onAddToLoad={row =>
        onAdd([
          {
            _id: row.id,
            serial: row.serial,
            type: row.type,
            cableName: row.cableName,
            lengthMeters: row.lengthMeters,
            weightKg: row.weightKg,
            physical: row.physical,
            source: 'storage',
          },
        ])
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Manual tab — cascading cable picker + cascading drum picker.
// ---------------------------------------------------------------------------

function ManualTab({ onAdd }) {
  const { data: standards, isLoading: loadingStd, error: stdError } = useQuery({
    queryKey: ['truck-sim', 'standards'],
    queryFn: fetchAllStandards,
    staleTime: 5 * 60 * 1000,
  });
  const { data: drumsResp, isLoading: loadingDrums } = useDrums();
  const drums = useMemo(
    () => (drumsResp?.data || []).filter(d => d.active !== false),
    [drumsResp]
  );

  // Cable selection state (cascading)
  const [cableName, setCableName] = useState('');
  const [cableConductors, setCableConductors] = useState('');
  const [cableSection, setCableSection] = useState('');
  const [cableColor, setCableColor] = useState('');
  const [cableGY, setCableGY] = useState('');

  // Drum selection state
  const [container, setContainer] = useState('drum');
  const [drumType, setDrumType] = useState('');
  const [drumDiameter, setDrumDiameter] = useState('');
  const [drumFlange, setDrumFlange] = useState('');

  const [meters, setMeters] = useState(1000);
  const [quantity, setQuantity] = useState(1);
  const [computing, setComputing] = useState(false);
  const [error, setError] = useState(null);

  // Cascading cable options --------------------------------------------------

  const cableNameOptions = useMemo(() => {
    if (!Array.isArray(standards)) return [];
    return uniqSorted(standards.map(s => s.name), (a, b) => a.localeCompare(b));
  }, [standards]);

  const conductorOptions = useMemo(() => {
    if (!cableName || !Array.isArray(standards)) return [];
    return uniqSorted(
      standards.filter(s => s.name === cableName).map(s => s.numberOfConductors)
    );
  }, [standards, cableName]);

  const sectionOptions = useMemo(() => {
    if (!cableName || !cableConductors || !Array.isArray(standards)) return [];
    return uniqSorted(
      standards
        .filter(s => s.name === cableName && Number(s.numberOfConductors) === Number(cableConductors))
        .map(s => s.section)
    );
  }, [standards, cableName, cableConductors]);

  const colorOptions = useMemo(() => {
    if (!cableName || !cableConductors || !cableSection || !Array.isArray(standards)) return [];
    return uniqSorted(
      standards
        .filter(s =>
          s.name === cableName &&
          Number(s.numberOfConductors) === Number(cableConductors) &&
          Number(s.section) === Number(cableSection)
        )
        .map(s => s.color),
      (a, b) => String(a).localeCompare(String(b))
    );
  }, [standards, cableName, cableConductors, cableSection]);

  const matchedStandard = useMemo(() => {
    if (!Array.isArray(standards)) return null;
    return standards.find(s =>
      s.name === cableName &&
      Number(s.numberOfConductors) === Number(cableConductors) &&
      Number(s.section) === Number(cableSection) &&
      String(s.color || '') === String(cableColor || '') &&
      (s.hasMixedConductorSections
        ? Number(s.greenYellowSection || 0) === Number(cableGY || 0)
        : true)
    );
  }, [standards, cableName, cableConductors, cableSection, cableColor, cableGY]);

  const gyOptionsForSelection = useMemo(() => {
    if (!matchedStandard && cableName && cableConductors && cableSection && cableColor) {
      // If no match yet, maybe a GY variant exists for this (name/cond/sec/color).
      return uniqSorted(
        (standards || [])
          .filter(s =>
            s.name === cableName &&
            Number(s.numberOfConductors) === Number(cableConductors) &&
            Number(s.section) === Number(cableSection) &&
            String(s.color || '') === String(cableColor || '') &&
            s.hasMixedConductorSections
          )
          .map(s => s.greenYellowSection)
      );
    }
    return [];
  }, [standards, cableName, cableConductors, cableSection, cableColor, matchedStandard]);

  // Reset downstream fields when an upstream one changes.
  const resetDownstream = (level) => {
    if (level <= 0) { setCableConductors(''); }
    if (level <= 1) { setCableSection(''); }
    if (level <= 2) { setCableColor(''); }
    if (level <= 3) { setCableGY(''); }
  };

  // Cascading drum options ---------------------------------------------------

  const drumTypeOptions = useMemo(
    () => uniqSorted(drums.map(d => d.drumType), (a, b) => String(a).localeCompare(String(b))),
    [drums]
  );

  const drumDiameterOptions = useMemo(() => {
    if (!drumType) return [];
    return uniqSorted(drums.filter(d => d.drumType === drumType).map(d => d.drumDiameter));
  }, [drums, drumType]);

  const drumFlangeOptions = useMemo(() => {
    if (!drumType || !drumDiameter) return [];
    return uniqSorted(
      drums
        .filter(d => d.drumType === drumType && Number(d.drumDiameter) === Number(drumDiameter))
        .map(d => d.flangeWidth)
    );
  }, [drums, drumType, drumDiameter]);

  const matchedDrum = useMemo(() => {
    if (!drumType || !drumDiameter) return null;
    const candidates = drums.filter(d =>
      d.drumType === drumType && Number(d.drumDiameter) === Number(drumDiameter)
    );
    if (candidates.length === 1) return candidates[0];
    if (!drumFlange) return null;
    return candidates.find(d => Number(d.flangeWidth) === Number(drumFlange)) || null;
  }, [drums, drumType, drumDiameter, drumFlange]);

  const resetDownstreamDrum = (level) => {
    if (level <= 0) { setDrumDiameter(''); }
    if (level <= 1) { setDrumFlange(''); }
  };

  // -------------------------------------------------------------------------

  const canSubmit =
    !!matchedStandard &&
    (container === 'europallet' || !!matchedDrum) &&
    Number(meters) > 0 &&
    Number(quantity) > 0;

  const addManual = async () => {
    setError(null);
    if (!matchedStandard) return setError('Pick a cable from the catalog.');
    if (container === 'drum' && !matchedDrum) return setError('Pick a drum.');

    try {
      setComputing(true);
      const weightResp = await fetchCableWeight({
        cableName: matchedStandard.name,
        numberOfConductors: matchedStandard.numberOfConductors,
        section: matchedStandard.section,
        color: matchedStandard.color,
        hasMixedConductorSections: !!matchedStandard.hasMixedConductorSections,
        greenYellowSection: matchedStandard.greenYellowSection,
        length: Number(meters),
      });

      const weightPerUnitKg = weightResp.totalWeightKg;

      if (container === 'drum' && matchedDrum.maxWeight
          && weightPerUnitKg > matchedDrum.maxWeight) {
        setError(
          `Per-drum weight ${weightPerUnitKg.toFixed(0)} kg exceeds drum ` +
          `max ${matchedDrum.maxWeight} kg. Reduce meters or pick a stronger drum.`
        );
        return;
      }

      const physical = container === 'drum'
        ? {
            flangeDiameterM: (matchedDrum.drumDiameter || 1200) / 1000,
            barrelLengthM: (matchedDrum.flangeWidth || 800) / 1000,
            drumType: matchedDrum.drumType,
          }
        : { ...EUROPALLET_PHYSICAL };

      const cableLabel =
        formatProductLabel(matchedStandard) +
        (matchedStandard.color ? ` (${matchedStandard.color})` : '');

      const serialPrefix = container === 'drum' ? 'MD' : 'ME';
      const stamp = Date.now().toString(36).slice(-4).toUpperCase();
      const picks = Array.from({ length: quantity }, (_, i) => ({
        _id: `manual-${stamp}-${i + 1}`,
        serial: `${serialPrefix}-${stamp}-${String(i + 1).padStart(2, '0')}`,
        type: container === 'drum' ? 'drum' : 'europallet',
        cableName: cableLabel,
        lengthMeters: Number(meters),
        weightKg: weightPerUnitKg,
        physical,
        source: 'manual',
      }));

      onAdd(picks);
    } catch (e) {
      setError(e?.message || 'Weight calculation failed.');
    } finally {
      setComputing(false);
    }
  };

  if (stdError) {
    return <Alert variant="danger">Failed to load standards: {stdError.message}</Alert>;
  }

  return (
    <Row>
      {/* Left column — cable standard */}
      <Col md={6}>
        <Card className="mb-3">
          <Card.Header className="fw-semibold">Cable standard</Card.Header>
          <Card.Body>
            <Row className="mb-2">
              <Col md={6}>
                <Form.Label>Cable name</Form.Label>
                <Form.Select
                  value={cableName}
                  onChange={e => { setCableName(e.target.value); resetDownstream(0); }}
                  disabled={loadingStd}
                >
                  <option value="">{loadingStd ? 'Loading…' : 'Select…'}</option>
                  {cableNameOptions.map(n => <option key={n} value={n}>{n}</option>)}
                </Form.Select>
              </Col>
              <Col md={6}>
                <Form.Label>Number of conductors</Form.Label>
                <Form.Select
                  value={cableConductors}
                  onChange={e => { setCableConductors(e.target.value); resetDownstream(1); }}
                  disabled={!cableName || conductorOptions.length === 0}
                >
                  <option value="">Select…</option>
                  {conductorOptions.map(n => <option key={n} value={n}>{n}</option>)}
                </Form.Select>
              </Col>
            </Row>
            <Row className="mb-2">
              <Col md={6}>
                <Form.Label>Section (mm²)</Form.Label>
                <Form.Select
                  value={cableSection}
                  onChange={e => { setCableSection(e.target.value); resetDownstream(2); }}
                  disabled={!cableConductors || sectionOptions.length === 0}
                >
                  <option value="">Select…</option>
                  {sectionOptions.map(s => <option key={s} value={s}>{s}</option>)}
                </Form.Select>
              </Col>
              <Col md={6}>
                <Form.Label>Color</Form.Label>
                <Form.Select
                  value={cableColor}
                  onChange={e => { setCableColor(e.target.value); resetDownstream(3); }}
                  disabled={!cableSection || colorOptions.length === 0}
                >
                  <option value="">Select…</option>
                  {colorOptions.map(c => <option key={c} value={c}>{c}</option>)}
                </Form.Select>
              </Col>
            </Row>
            {gyOptionsForSelection.length > 0 && (
              <Row>
                <Col md={6}>
                  <Form.Label>Green/Yellow section (mm²)</Form.Label>
                  <Form.Select
                    value={cableGY}
                    onChange={e => setCableGY(e.target.value)}
                  >
                    <option value="">Select…</option>
                    {gyOptionsForSelection.map(s => <option key={s} value={s}>{s}</option>)}
                  </Form.Select>
                </Col>
              </Row>
            )}

            {matchedStandard ? (
              <div className="mt-2 small text-success">
                ✓ Matched: <code>{formatProductLabel(matchedStandard)}</code>
                {matchedStandard.color && ` (${matchedStandard.color})`}
                {matchedStandard.hasMixedConductorSections && ` GY${matchedStandard.greenYellowSection}`}
              </div>
            ) : (
              (cableName || cableConductors || cableSection) && (
                <div className="mt-2 small text-muted">
                  Keep selecting — no standard match yet.
                </div>
              )
            )}
          </Card.Body>
        </Card>
      </Col>

      {/* Right column — container + quantity */}
      <Col md={6}>
        <Card className="mb-3">
          <Card.Header className="fw-semibold">Container</Card.Header>
          <Card.Body>
            <Form.Group className="mb-2">
              <div>
                <Form.Check
                  inline type="radio" name="container" label="Drum"
                  checked={container === 'drum'}
                  onChange={() => setContainer('drum')}
                />
                <Form.Check
                  inline type="radio" name="container" label="Coil on europallet"
                  checked={container === 'europallet'}
                  onChange={() => setContainer('europallet')}
                />
              </div>
            </Form.Group>

            {container === 'drum' && (
              <>
                <Row className="mb-2">
                  <Col md={6}>
                    <Form.Label>Drum type</Form.Label>
                    <Form.Select
                      value={drumType}
                      onChange={e => { setDrumType(e.target.value); resetDownstreamDrum(0); }}
                      disabled={loadingDrums}
                    >
                      <option value="">{loadingDrums ? 'Loading…' : 'Select…'}</option>
                      {drumTypeOptions.map(t => (
                        <option key={t} value={t} className="text-capitalize">{t}</option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={6}>
                    <Form.Label>Drum diameter (mm)</Form.Label>
                    <Form.Select
                      value={drumDiameter}
                      onChange={e => { setDrumDiameter(e.target.value); resetDownstreamDrum(1); }}
                      disabled={!drumType || drumDiameterOptions.length === 0}
                    >
                      <option value="">Select…</option>
                      {drumDiameterOptions.map(d => <option key={d} value={d}>{d}</option>)}
                    </Form.Select>
                  </Col>
                </Row>
                {drumFlangeOptions.length > 1 && (
                  <Row>
                    <Col md={6}>
                      <Form.Label>Flange width (mm)</Form.Label>
                      <Form.Select
                        value={drumFlange}
                        onChange={e => setDrumFlange(e.target.value)}
                      >
                        <option value="">Select…</option>
                        {drumFlangeOptions.map(f => <option key={f} value={f}>{f}</option>)}
                      </Form.Select>
                    </Col>
                  </Row>
                )}
                {matchedDrum && (
                  <div className="mt-2 small text-success">
                    ✓ Drum: Ø{matchedDrum.drumDiameter}mm · flange {matchedDrum.flangeWidth}mm
                    {matchedDrum.barrelDiameter ? ` · barrel ${matchedDrum.barrelDiameter}mm` : ''}
                    {matchedDrum.maxWeight ? ` · max ${matchedDrum.maxWeight} kg` : ''}
                  </div>
                )}
              </>
            )}
          </Card.Body>
        </Card>

        <Card>
          <Card.Header className="fw-semibold">Quantity</Card.Header>
          <Card.Body>
            <Row>
              <Col xs={6}>
                <Form.Label>Meters per unit</Form.Label>
                <Form.Control
                  type="number" min={1} step={50}
                  value={meters}
                  onChange={e => setMeters(Number(e.target.value))}
                />
              </Col>
              <Col xs={6}>
                <Form.Label>Quantity</Form.Label>
                <Form.Control
                  type="number" min={1} step={1}
                  value={quantity}
                  onChange={e => setQuantity(Number(e.target.value))}
                />
              </Col>
            </Row>

            {error && <Alert variant="danger" className="py-2 mt-2 mb-2">{error}</Alert>}

            <div className="mt-3 text-end">
              <Button
                variant="primary"
                onClick={addManual}
                disabled={computing || !canSubmit}
              >
                {computing
                  ? <><Spinner size="sm" animation="border" className="me-2" />Computing…</>
                  : `Add ${quantity || 1} × to load`}
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
}
